/**
 * Ensures IndexedDB database is properly initialized with object store
 * @returns {Promise<IDBDatabase>} - Database instance
 */
async function ensureIndexedDBInitialized() {
    return new Promise((resolve, reject) => {
        if (!('indexedDB' in window)) {
            reject(new Error('IndexedDB not supported'));
            return;
        }
        
        const request = indexedDB.open('GenieProductImages', 1);
        
        request.onerror = () => {
            reject(new Error('Failed to open IndexedDB'));
        };
        
        request.onsuccess = (event) => {
            const db = event.target.result;
            
            // Check if object store exists
            if (!db.objectStoreNames.contains('images')) {
                // Close and delete database, then retry
                db.close();
                const deleteRequest = indexedDB.deleteDatabase('GenieProductImages');
                
                deleteRequest.onsuccess = () => {
                    // Retry opening with upgrade
                    const retryRequest = indexedDB.open('GenieProductImages', 1);
                    retryRequest.onsuccess = (e) => resolve(e.target.result);
                    retryRequest.onerror = () => reject(new Error('Failed to recreate database'));
                    retryRequest.onupgradeneeded = (e) => {
                        const retryDb = e.target.result;
                        if (!retryDb.objectStoreNames.contains('images')) {
                            const objectStore = retryDb.createObjectStore('images', { keyPath: 'key' });
                            objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                        }
                    };
                };
                
                deleteRequest.onerror = () => {
                    reject(new Error('Failed to delete corrupted database'));
                };
            } else {
                resolve(db);
            }
        };
        
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains('images')) {
                const objectStore = db.createObjectStore('images', { keyPath: 'key' });
                // Create index for cleanup (older than 7 days)
                objectStore.createIndex('timestamp', 'timestamp', { unique: false });
            }
        };
    });
}

/**
 * Stores image data in IndexedDB
 * @param {string} key - Unique key for the image
 * @param {string} imageData - Base64 image data
 */
async function storeImageInIndexedDB(key, imageData) {
    return new Promise((resolve, reject) => {
        if (!('indexedDB' in window)) {
            // Fallback to localStorage if IndexedDB not available
            try {
                localStorage.setItem(key, imageData);
                resolve();
            } catch (e) {
                console.warn('Could not store image in localStorage:', e);
                reject(e);
            }
            return;
        }
        
        ensureIndexedDBInitialized()
            .then((db) => {
                try {
                    const transaction = db.transaction(['images'], 'readwrite');
                    const store = transaction.objectStore('images');
                    const putRequest = store.put({ key: key, data: imageData, timestamp: Date.now() });
                    
                    putRequest.onsuccess = () => resolve();
                    putRequest.onerror = () => {
                        // Fallback to localStorage
                        try {
                            localStorage.setItem(key, imageData);
                            resolve();
                        } catch (e) {
                            reject(e);
                        }
                    };
                } catch (error) {
                    // Fallback to localStorage on transaction error
                    try {
                        localStorage.setItem(key, imageData);
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                }
            })
            .catch((error) => {
                // Fallback to localStorage if database initialization fails
                console.warn('IndexedDB initialization failed, falling back to localStorage:', error);
                try {
                    localStorage.setItem(key, imageData);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
    });
}

/**
 * Retrieves image data from IndexedDB or localStorage
 * @param {string} key - Unique key for the image
 * @returns {Promise<string|null>} - Base64 image data or null
 */
async function getImageFromStorage(key) {
    return new Promise((resolve) => {
        if (!('indexedDB' in window)) {
            // Fallback to localStorage
            try {
                const data = localStorage.getItem(key);
                resolve(data);
            } catch (e) {
                resolve(null);
            }
            return;
        }
        
        ensureIndexedDBInitialized()
            .then((db) => {
                try {
                    const transaction = db.transaction(['images'], 'readonly');
                    const store = transaction.objectStore('images');
                    const getRequest = store.get(key);
                    
                    getRequest.onsuccess = () => {
                        if (getRequest.result) {
                            resolve(getRequest.result.data);
                        } else {
                            // Try localStorage as fallback
                            try {
                                const data = localStorage.getItem(key);
                                resolve(data);
                            } catch (e) {
                                resolve(null);
                            }
                        }
                    };
                    
                    getRequest.onerror = () => {
                        // Fallback to localStorage
                        try {
                            const data = localStorage.getItem(key);
                            resolve(data);
                        } catch (e) {
                            resolve(null);
                        }
                    };
                } catch (error) {
                    // Fallback to localStorage on transaction error
                    try {
                        const data = localStorage.getItem(key);
                        resolve(data);
                    } catch (e) {
                        resolve(null);
                    }
                }
            })
            .catch(() => {
                // Fallback to localStorage if database initialization fails
                try {
                    const data = localStorage.getItem(key);
                    resolve(data);
                } catch (e) {
                    resolve(null);
                }
            });
    });
}

/**
 * Cleans up old images from IndexedDB
 * Removes images older than 30 days or not referenced in current cart
 */
async function cleanupOldImages() {
    try {
        // Get current cart to see which images are still needed
        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!cartResponse.ok) return;
        
        const cartData = await cartResponse.json();
        const activeImageKeys = new Set();
        
        // Collect all active image keys from cart
        if (cartData.items) {
            cartData.items.forEach(item => {
                if (item.properties && item.properties._imageKey) {
                    activeImageKeys.add(item.properties._imageKey);
                }
            });
        }
        
        if (!('indexedDB' in window)) return;
        
        try {
            const db = await ensureIndexedDBInitialized();
            
            const transaction = db.transaction(['images'], 'readwrite');
            const store = transaction.objectStore('images');
            
            // Check if index exists before using it
            let index;
            try {
                index = store.index('timestamp');
            } catch (e) {
                // Index doesn't exist, continue without it
                index = null;
            }
            
            const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
            
            // Get all images
            const getAllRequest = store.getAll();
            
            getAllRequest.onsuccess = () => {
                const images = getAllRequest.result;
                let deletedCount = 0;
                
                images.forEach(image => {
                    const shouldDelete = 
                        // Image is older than 30 days
                        (image.timestamp < thirtyDaysAgo) ||
                        // Image is not in active cart
                        (!activeImageKeys.has(image.key));
                    
                    if (shouldDelete) {
                        store.delete(image.key);
                        deletedCount++;
                    }
                });
                
                if (deletedCount > 0) {
                    console.log(`Cleaned up ${deletedCount} old product images`);
                }
            };
            
            getAllRequest.onerror = () => {
                // Silently fail cleanup
            };
        } catch (error) {
            // Silently fail if database initialization fails
        }
    } catch (error) {
        console.warn('Error cleaning up old images:', error);
    }
}

/**
 * Removes specific image from storage
 * @param {string} key - Image key to remove
 */
async function removeImageFromStorage(key) {
    if (!key) return;
    
    try {
        if (!('indexedDB' in window)) {
            // Fallback to localStorage
            localStorage.removeItem(key);
            return;
        }
        
        ensureIndexedDBInitialized()
            .then((db) => {
                try {
                    const transaction = db.transaction(['images'], 'readwrite');
                    const store = transaction.objectStore('images');
                    store.delete(key);
                } catch (error) {
                    // Silently fail if deletion fails
                }
            })
            .catch(() => {
                // Silently fail if database initialization fails
            });
    } catch (error) {
        console.warn('Error removing image:', error);
    }
}

/** Prevents MutationObserver from re-triggering when we replace images (avoids cart.js being called 100+ times) */
let isUpdatingCartItemImages = false;

/**
 * Updates cart item images from stored base64 data
 */
async function updateCartItemImages() {
    if (isUpdatingCartItemImages) return;
    isUpdatingCartItemImages = true;
    try {
        // Fetch current cart data
        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!cartResponse.ok) return;
        
        const cartData = await cartResponse.json();
        if (!cartData.items || cartData.items.length === 0) return;
        
        // Map line item index to image: prefer _imageUrl (URL), fallback to _imageKey (IndexedDB) for old items
        const lineItemImageMap = {};
        cartData.items.forEach((item, index) => {
            const props = item.properties || {};
            if (props._imageUrl) {
                lineItemImageMap[index] = { type: 'url', value: props._imageUrl };
                lineItemImageMap[index + 1] = { type: 'url', value: props._imageUrl };
            } else if (props._imageKey) {
                lineItemImageMap[index] = { type: 'key', value: props._imageKey };
                lineItemImageMap[index + 1] = { type: 'key', value: props._imageKey };
            }
        });

        function setCartItemImage(currentCartItem, imageSrc) {
            const imageContainer = currentCartItem.querySelector('.cart-item__image a');
            if (!imageContainer) return;
            const existingImg = imageContainer.querySelector('img');
            if (!existingImg) return;
            const img = document.createElement('img');
            img.alt = existingImg.alt || 'Customized product preview';
            if (existingImg.style.width) img.style.width = existingImg.style.width;
            if (existingImg.style.height) img.style.height = existingImg.style.height;
            if (existingImg.style.maxHeight) img.style.maxHeight = existingImg.style.maxHeight;
            if (existingImg.style.objectFit) img.style.objectFit = existingImg.style.objectFit;
            if (!img.style.width) img.style.width = '100%';
            if (!img.style.height) img.style.height = 'auto';
            if (!img.style.maxHeight) img.style.maxHeight = '200px';
            if (!img.style.objectFit) img.style.objectFit = 'contain';
            let imageReplaced = false;
            const replaceImage = () => {
                if (imageReplaced) return;
                if (!img.complete || (img.naturalHeight === 0 && img.naturalWidth === 0)) return;
                const container = currentCartItem.querySelector('.cart-item__image a');
                const currentImg = container && container.querySelector('img');
                if (currentImg) { currentImg.replaceWith(img); imageReplaced = true; }
            };
            img.onload = replaceImage;
            img.onerror = function () {};
            img.src = imageSrc;
            if (img.complete && img.naturalHeight > 0 && img.naturalWidth > 0) replaceImage();
            else setTimeout(function () { if (!imageReplaced && img.complete && img.naturalHeight > 0) replaceImage(); }, 50);
        }

        // Update each cart item
        const cartItems = document.querySelectorAll('.cart-item');
        cartItems.forEach((cartItem) => {
            const cartItemId = cartItem.id.match(/cart-item-(\d+)/);
            if (!cartItemId) return;
            const itemIndex = parseInt(cartItemId[1]) - 1;
            const imageInfo = lineItemImageMap[itemIndex] || lineItemImageMap[parseInt(cartItemId[1])];
            if (!imageInfo) return;
            if (imageInfo.type === 'url') {
                setCartItemImage(cartItem, imageInfo.value);
                return;
            }
            // Fallback: _imageKey (IndexedDB) for older cart items
            (async (currentCartItem, imageKey) => {
                const imageData = await getImageFromStorage(imageKey);
                if (!imageData) return;
                let cleanImageData = imageData.trim().replace(/\s/g, '');
                if (!cleanImageData) return;
                const dataUrl = cleanImageData.startsWith('data:') ? cleanImageData : `data:image/png;base64,${cleanImageData}`;
                setCartItemImage(currentCartItem, dataUrl);
            })(cartItem, imageInfo.value);
        });
    } catch (error) {
        console.warn('Could not update cart item images:', error);
    } finally {
        isUpdatingCartItemImages = false;
    }
}

// Setup MutationObserver for cart drawer (can be called multiple times safely)
let cartDrawerObserver = null;
function setupCartDrawerObserver() {
    // Remove existing observer if it exists
    if (cartDrawerObserver) {
        cartDrawerObserver.disconnect();
        cartDrawerObserver = null;
    }
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
        // Use MutationObserver to watch for cart drawer content changes (e.g. new HTML from server).
        // Ignore mutations we caused ourselves (image replacement) to avoid infinite loop / 100+ cart.js calls.
        cartDrawerObserver = new MutationObserver((mutations) => {
            if (isUpdatingCartItemImages) return;
            // Only react to mutations that look like cart content refresh (many nodes), not our single img swap
            const hasNonImageMutation = mutations.some((m) => {
                const target = m.target;
                if (!target || !target.closest) return true;
                const inImageCell = target.closest('.cart-item__image');
                if (!inImageCell) return true;
                const onlyImgChange = (m.addedNodes.length === 1 && m.removedNodes.length === 1 &&
                    m.addedNodes[0].nodeName === 'IMG' && m.removedNodes[0].nodeName === 'IMG');
                return !onlyImgChange;
            });
            if (hasNonImageMutation) {
                setTimeout(() => updateCartItemImages(), 100);
            }
        });
        
        cartDrawerObserver.observe(cartDrawer, {
            childList: true,
            subtree: true
        });
    }
}

// Listen for cart drawer updates and page loads
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(function() {
        if (document.getElementById('tabbed_panel-root_tabs') != null) {
            return;
        }
        const customizeProductId = document.getElementById('customize_product_id');
        if (customizeProductId && customizeProductId.value) {
            window.location.href = customizeProductId.value;
        }
    }, 3000);
    
    updateCartItemImages();
    
    // Run cleanup on page load (once per day)
    const lastCleanup = localStorage.getItem('genie-last-cleanup');
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    
    if (!lastCleanup || parseInt(lastCleanup) < oneDayAgo) {
        cleanupOldImages();
        localStorage.setItem('genie-last-cleanup', Date.now().toString());
    }
    
    // Setup MutationObserver for cart drawer
    setupCartDrawerObserver();
    
    // Listen for cart drawer open event - ensures images update when drawer opens from any page
    document.addEventListener('on:cart-drawer:after-open', () => {
        // Re-setup observer in case cart drawer was re-rendered
        setupCartDrawerObserver();
        // Wait a bit for the drawer content to render
        setTimeout(() => updateCartItemImages(), 200);
    });
    
    // Listen for cart update events
    document.addEventListener('on:cart:add', () => {
        setTimeout(() => updateCartItemImages(), 300);
    });
    
    document.addEventListener('on:line-item:change', (event) => {
        setTimeout(() => updateCartItemImages(), 300);
        
        // If quantity is 0, item was removed - clean up its image
        if (event.detail.newQuantity === 0) {
            // Get the removed item's image key and clean it up
            // Note: This requires fetching cart data to get the property
            setTimeout(async () => {
                try {
                    const cartResponse = await fetch(`${theme.routes.cart}.js`);
                    if (cartResponse.ok) {
                        const cartData = await cartResponse.json();
                        // Cleanup will handle removing unused images
                        cleanupOldImages();
                    }
                } catch (error) {
                    console.warn('Error cleaning up removed item image:', error);
                }
            }, 500);
        }
    });
    
    // Also update on cart page
    if (window.location.pathname === theme.routes.cart) {
        setTimeout(() => updateCartItemImages(), 500);
    }
});

window.addEventListener('configuration.addToCart', async (e) => {
    console.log(e.detail);
    // Create and show loader
    let loader = document.getElementById('genie-loader');
    if (!loader) {
        loader = document.createElement('div');
        loader.id = 'genie-loader';
        loader.className = 'genie-loader';
        loader.innerHTML = '<div class="genie-loader__spinner"></div>';
        document.body.appendChild(loader);
    }
    loader.classList.add('is-visible');
    
    try {
        // Parse the XML
    const parser = new DOMParser();
   const doc = parser.parseFromString(e.detail, "text/xml");
        console.log(doc);
        // Get the product form to extract variant ID
        const productForm = document.querySelector('.js-product-form');
        if (!productForm) {
            console.error('Product form not found');
            loader.classList.remove('is-visible');
            return;
        }
        
        // Get variant ID from the form
        const variantIdInput = productForm.querySelector('input[name="id"]');
        if (!variantIdInput || !variantIdInput.value) {
            console.error('Variant ID not found');
            loader.classList.remove('is-visible');
            return;
        }
        const variantId = variantIdInput.value;
        
        // Get quantity (default to 1 if not found)
        const quantityInput = productForm.querySelector('input[name="quantity"]');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        
        // Helper: normalize base64 string (strip data URL prefix, whitespace)
        function toCleanBase64(raw) {
            if (!raw) return null;
            let s = raw.trim().replace(/\s/g, '');
            if (s.startsWith('data:')) {
                const i = s.indexOf('base64,');
                if (i !== -1) s = s.substring(i + 7);
            }
            return s || null;
        }
        function isHttpImageUrl(s) {
            if (!s || typeof s !== 'string') return false;
            return /^https?:\/\//i.test(s.trim());
        }
        // Optional: upload base64 for legacy main product image only (attribute images use server URLs in XML)
        async function uploadBase64AndGetUrl(base64) {
            const res = await fetch('https://ipcoop.genielab.uk/json/upload-base64-image', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image: base64 })
            });
            if (!res.ok) return null;
            const json = await res.json();
            return json.url || json.imageUrl || json.image_url || null;
        }

        // Extract attributes (text values + attribute <image> as direct URL, e.g. https://byop.../genie_....png)
        const attributes = [];
        const attributeNodes = doc.querySelectorAll('attributes attribute');
        
        for (const attr of attributeNodes) {
            const name = attr.querySelector('name')?.textContent?.trim();
            const valueEl = attr.querySelector('value');
            const imageEl = attr.querySelector('image');
            const valueText = valueEl?.textContent?.trim();
            const imageRaw = imageEl?.textContent?.trim() || imageEl?.querySelector('imageData')?.textContent?.trim();
            
            if (!name) continue;
            
            if (valueText) {
                attributes.push({ name: name, value: valueText });
            } else if (imageRaw && isHttpImageUrl(imageRaw)) {
                attributes.push({ name: '_' + name, value: imageRaw.trim() });
            }
        }
        
        // Extract editUrl and unitPrice
        const editUrl = doc.querySelector('editUrl')?.textContent?.trim();
        let unitPrice = doc.querySelector('unitPrice')?.textContent?.trim();
        const isB2B = document.querySelector('#main-content.b2b') !== null;
        if (unitPrice && isB2B) {
            const numericPrice = parseFloat(unitPrice.replace(/,/g, ''), 10);
            if (!Number.isNaN(numericPrice)) {
                unitPrice = (numericPrice * 0.65).toFixed(2);
            }
        }
        
        // Main product image: URL from XML when provided, else legacy base64 upload
        let productImageUrl = null;
        const productImages = doc.querySelector('productImages');
        if (productImages) {
            const imageData = productImages.querySelector('imageData')?.textContent?.trim();
            if (imageData) {
                if (isHttpImageUrl(imageData)) {
                    productImageUrl = imageData.trim();
                } else {
                    const cleanImageData = toCleanBase64(imageData);
                    if (cleanImageData) {
                        try {
                            productImageUrl = await uploadBase64AndGetUrl(cleanImageData);
                        } catch (err) {
                            console.warn('Product image upload failed:', err);
                        }
                    }
                }
            }
        }
        
        // Create FormData for cart addition
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
        
        const deliveryEl = document.getElementById('add_hidden_delivery');
        if (deliveryEl && deliveryEl.value) {
            formData.append('properties[Order Estimation]', deliveryEl.value);
        }
        formData.append('properties[_Note]', 'Important:  Order Notes will show Post Purchase changes to the order details.');
        
        // Add all attributes as line item properties
        attributes.forEach(attr => {
            formData.append(`properties[${attr.name}]`, attr.value);
        });
        
        // Add hidden properties (prefixed with underscore)
        if (editUrl) {
            formData.append('properties[_editUrl]', editUrl);
        }
        
        if (unitPrice) {
            formData.append('properties[_unitPrice]', unitPrice);
        }
        
        // Product image URL: used for cart drawer, cart page, and admin (no _imageKey / IndexedDB)
        if (productImageUrl) {
            formData.append('properties[_imageUrl]', productImageUrl);
        }
        
        // Get sections to update
        let sections = 'cart-icon-bubble';
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
            sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
        }
        
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);
        
        // Get old cart state
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();
        
        // Add to cart
        const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
        });
        
        const data = await response.json();
        
        // Check for errors
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }
        
        if (data.status) {
            console.error('Cart error:', error);
            loader.classList.remove('is-visible');
            // You might want to show an error message to the user here
            return;
        }
        
        if (!response.ok) throw new Error(response.status);
        
        // Update cart icon
        if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) {
            ProductForm.updateCartIcon(data);
        }
        
        // Update cart drawer or page
        setTimeout(() => {
            if (cartDrawer) {
                cartDrawer.renderContents(
                    data,
                    theme.settings.afterAtc === 'drawer'
                );
                // Update product images after drawer renders
                setTimeout(() => {
                    updateCartItemImages();
                }, 100);
            } else if (window.location.pathname === theme.routes.cart) {
                const cartItems = document.querySelector('cart-items');
                if (cartItems) {
                    if (cartItems.dataset.empty === 'true') {
                        window.location.reload();
                    } else {
                        cartItems.refresh();
                        // Update product images after cart page refreshes
                        setTimeout(() => {
                            updateCartItemImages();
                        }, 100);
                    }
                }
            }
        }, 700);
        
        // Get new cart state and dispatch events
        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        
        const itemInOldCart = oldCartData.items.filter(
            (item) => item.variant_id === data.variant_id
        )[0];
        
        // Dispatch cart events
        if (itemInOldCart) {
            document.dispatchEvent(new CustomEvent('on:line-item:change', {
                bubbles: true,
                detail: {
                    cart: newCartData,
                    variantId: data.variant_id,
                    oldQuantity: itemInOldCart.quantity,
                    newQuantity: (itemInOldCart.quantity === data.quantity)
                        ? itemInOldCart.quantity : data.quantity
                }
            }));
        } else {
            document.dispatchEvent(new CustomEvent('on:cart:add', {
                bubbles: true,
                detail: {
                    cart: newCartData,
                    variantId: data.variant_id
                }
            }));
        }
        
        console.log('Item added to cart successfully');
        
        // Hide loader after successful addition
        loader.classList.remove('is-visible');
        
    } catch (error) {
        console.error('Error adding to cart:', error);
        loader.classList.remove('is-visible');
        document.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
                error: error.message
            }
        }));
    }
 });